import React, { useState, useEffect } from 'react';
import { MapPin, Calendar, ChevronDown, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { userInterestsApi } from '../lib/api';
import { supabase } from '../lib/supabase';
import { RankingColumn, MONTHS_IT } from './MonthlyRanking';
import type { ContactInfo, MonthlyRankingEntry } from './MonthlyRanking';

const CityMonthlyRanking = () => {
  const { user } = useAuth();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [womenEteroRanking, setWomenEteroRanking] = useState<MonthlyRankingEntry[]>([]);
  const [menEteroRanking, setMenEteroRanking] = useState<MonthlyRankingEntry[]>([]);
  const [womenGayRanking, setWomenGayRanking] = useState<MonthlyRankingEntry[]>([]);
  const [menGayRanking, setMenGayRanking] = useState<MonthlyRankingEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCities, setLoadingCities] = useState(true);

  useEffect(() => {
    loadCities();
  }, []);

  useEffect(() => {
    if (selectedCity) {
      loadCityData();
    }
  }, [selectedMonth, selectedYear, selectedCity]);

  const loadCities = async () => {
    try {
      setLoadingCities(true);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('city')
        .not('city', 'is', null)
        .neq('city', '');

      const cities = [...new Set(
        (profiles || [])
          .map((p: any) => p.city)
          .filter((c: string | null) => c && c.trim() !== '')
      )].sort() as string[];

      setAvailableCities(cities);

      if (user?.role === 'admin') {
        if (cities.length > 0) setSelectedCity(cities[0]);
      } else if (user?.city) {
        const userCity = cities.find(c => c === user.city);
        setSelectedCity(userCity || (cities.length > 0 ? cities[0] : ''));
      } else if (cities.length > 0) {
        setSelectedCity(cities[0]);
      }
    } catch (err) {
      console.error('Error loading cities:', err);
    } finally {
      setLoadingCities(false);
    }
  };

  const loadCityData = async () => {
    if (!selectedCity) return;
    try {
      setLoading(true);
      const startDate = new Date(selectedYear, selectedMonth, 1).toISOString();
      const endDate = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59).toISOString();

      const data = await userInterestsApi.getByDateRange(startDate, endDate);

      const cityFiltered = (data || []).filter(
        (interest: any) => interest.event?.city === selectedCity
      );

      const receivedMap = new Map<string, number>();
      const contactMap = new Map<string, ContactInfo>();

      cityFiltered.forEach((interest: any) => {
        if (interest.to_user) {
          const toId = interest.to_user.id;
          contactMap.set(toId, interest.to_user);
          receivedMap.set(toId, (receivedMap.get(toId) || 0) + 1);
        }
      });

      const allEntries: MonthlyRankingEntry[] = Array.from(contactMap.entries()).map(([id, contact]) => ({
        contact,
        receivedCount: receivedMap.get(id) || 0,
      }));

      const sortDesc = (a: MonthlyRankingEntry, b: MonthlyRankingEntry) => b.receivedCount - a.receivedCount;

      setWomenEteroRanking(allEntries.filter(e => e.contact.gender === 'donna_etero').sort(sortDesc));
      setMenEteroRanking(allEntries.filter(e => e.contact.gender === 'uomo_etero').sort(sortDesc));
      setWomenGayRanking(allEntries.filter(e => e.contact.gender === 'donna_gay').sort(sortDesc));
      setMenGayRanking(allEntries.filter(e => e.contact.gender === 'uomo_gay').sort(sortDesc));
    } catch (err) {
      console.error('Error loading city ranking:', err);
    } finally {
      setLoading(false);
    }
  };

  const goToPrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(prev => prev - 1);
    } else {
      setSelectedMonth(prev => prev - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(prev => prev + 1);
    } else {
      setSelectedMonth(prev => prev + 1);
    }
  };

  const isCurrentMonth = selectedMonth === now.getMonth() && selectedYear === now.getFullYear();

  const yearOptions: number[] = [];
  for (let y = 2024; y <= now.getFullYear() + 1; y++) {
    yearOptions.push(y);
  }

  if (loadingCities) {
    return (
      <div className="mt-8 flex justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-rose-400" />
      </div>
    );
  }

  if (availableCities.length === 0) return null;

  return (
    <div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2 text-lg whitespace-nowrap uppercase">
                <MapPin className="h-5 w-5 text-rose-500 flex-shrink-0" />
                Classifica Mensile per Citta
              </h2>
              {user?.role === 'admin' ? (
                <div className="relative flex-shrink-0">
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="appearance-none bg-rose-50 border border-rose-200 rounded-lg px-3 py-1.5 pr-8 text-sm font-semibold text-rose-700 uppercase focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    {availableCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-rose-400 pointer-events-none" />
                </div>
              ) : (
                <span className="text-sm font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-3 py-1.5 rounded-lg flex-shrink-0 uppercase">
                  {selectedCity}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={goToPrevMonth}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex gap-2">
                <div className="relative">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    {MONTHS_IT.map((month, idx) => (
                      <option key={idx} value={idx}>{month}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                <div className="relative">
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="appearance-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    {yearOptions.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <button
                onClick={goToNextMonth}
                disabled={isCurrentMonth}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <RankingColumn
          title="Classifica Donne Etero"
          color="rose"
          entries={womenEteroRanking}
          loading={loading}
        />
        <RankingColumn
          title="Classifica Uomini Etero"
          color="sky"
          entries={menEteroRanking}
          loading={loading}
        />
        <RankingColumn
          title="Classifica Donne Gay"
          color="rose"
          entries={womenGayRanking}
          loading={loading}
        />
        <RankingColumn
          title="Classifica Uomini Gay"
          color="sky"
          entries={menGayRanking}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default CityMonthlyRanking;
